import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getLocalizedItems, getTranslatedItems } from '@/plugins/i18n'

import { accountingApi } from '@/apis/accounting.api'

import { useGetOrFetchAsync } from '@/composables/store'

export const useAccountingStore = defineStore('accounting', () => {
  const api = accountingApi
  const financialCenters = ref([])
  const tradeAccounts = ref([])
  const tradeAccountFinancialCenterAssociations = ref([])

  const financialCenterTypesEnum = Object.freeze({
    COST: 0,
    INCOME: 1,
    COST_INCOME: 2,
  })

  const tradeAccountTypesEnum = Object.freeze({
    PURCHASE: 0,
    SALE: 1,
    PURCHASE_SALE: 2,
  })

  const customerAccountingStatusTypesEnum = Object.freeze({
    ACTIVE: 0,
    BLOCKED: 1,
    DISPUTED: 2,
  })

  const fetchFinancialCentersAsync = async(force = false) => useGetOrFetchAsync(financialCenters, accountingApi.getFinancialCentersAsync, force)
  const fetchTradeAccountsAsync = async(force = false) => useGetOrFetchAsync(tradeAccounts, accountingApi.getTradeAccountsAsync, force)
  const fetchTradeAccountFinancialCenterAssociationsAsync = async(force = false) => useGetOrFetchAsync(tradeAccountFinancialCenterAssociations, accountingApi.getTradeAccountFinancialCenterAssociationsAsync, force)

  const getLocalizedFinancialCenters = () => getTranslatedItems(financialCenters.value)
  const getLocalizedTradeAccounts = () => getTranslatedItems(tradeAccounts.value)
  const getLocalizedCustomerAccountingStatusTypesEnum = () => getLocalizedItems(Object.keys(customerAccountingStatusTypesEnum).map(x => ({
    id: customerAccountingStatusTypesEnum[x],
    name: `common.customerAccountingStatusTypesEnum.${x.toLowerCase()}`
  })))

  const getFinancialCenterIdsByTradeAccountId = (tradeAccountId) => {
    const associations = tradeAccountFinancialCenterAssociations.value
    const allFinancialCenterIds = financialCenters.value.map(fc => fc.id)
    if (!tradeAccountId) {
      return [...allFinancialCenterIds]
    }

    const filteredFinancialCenterIds = associations
      .filter(assoc => assoc.trade_account_ref === tradeAccountId)
      .map(assoc => assoc.financial_center_ref)

    return filteredFinancialCenterIds.length > 0 ? filteredFinancialCenterIds : [...allFinancialCenterIds]
  }

  const getTradeAccountIdsByFinancialCenterId = (financialCenterId) => {
    const associations = tradeAccountFinancialCenterAssociations.value
    if (!financialCenterId) {
      return [...tradeAccounts.value.map(ta => ta.id)]
    }

    const result = associations
      .filter(assoc => assoc.financial_center_ref === financialCenterId)
      .map(assoc => assoc.trade_account_ref)

    return result.length > 0 ? result : [...tradeAccounts.value.map(ta => ta.id)]
  }

  const $reset = () => {
    financialCenters.value = []
    tradeAccounts.value = []
    tradeAccountFinancialCenterAssociations.value = []
  }

  return {
    api,
    customerAccountingStatusTypesEnum,
    financialCenterTypesEnum,
    financialCenters,
    tradeAccountTypesEnum,
    tradeAccounts,
    tradeAccountFinancialCenterAssociations,
    fetchFinancialCentersAsync,
    fetchTradeAccountsAsync,
    fetchTradeAccountFinancialCenterAssociationsAsync,
    getFinancialCenterIdsByTradeAccountId,
    getTradeAccountIdsByFinancialCenterId,
    getLocalizedFinancialCenters,
    getLocalizedTradeAccounts,
    getLocalizedCustomerAccountingStatusTypesEnum,
    $reset
  }
}, {
  persist: [
    {
      pick: ['financialCenters', 'tradeAccounts', 'tradeAccountFinancialCenterAssociations'],
      storage: localStorage
    }
  ]
})
